import { Camera, TransformPosition, TransformRotation } from '@ikea-aoa/elements-3d';
import {
	View3dElement,
	ConnectableObject,
	TransformVector,
	ConnectionManager,
} from '@ikea-aoa/component-view-3d';
import { ModuleConnectionManager } from '../../util/SceneUtils/ModuleConnectionManager';
import { DeviceType } from 'sofa-reborn-react';
import { DebounceManager } from '@inter-ikea-kompis/utilities';

const SCREENSHOT_CAMERA_NAME = 'screenshotCamera';
const MAIN_CAMERA_ANIMATION_TIME = 300;
const SCREENSHOT_CAMERA_ANIMATION_TIME = 0;
const CAMERAS_MIN_DISTANCE = 31;

type Props = {
	showSelectAll?: boolean | undefined;
	showMeasurements?: boolean | undefined;
	deviceType?: DeviceType | undefined;
};

export class CameraController {
	private cameraTimeout: number;
	private mainCamera: Camera;
	private screenshotCamera: Camera;
	private sceneItems: Map<string, ConnectableObject>;
	private connectionManager: ConnectionManager;
	private debounce = new DebounceManager();

	constructor(
		private view3d: View3dElement,
		private moduleManager: ModuleConnectionManager,
		private props: Props
	) {
		this.sceneItems = this.moduleManager.sceneItems;
		this.connectionManager = this.moduleManager.connectionManager;

		this.InitializeCameras();
	}

	/**
	 * Update camera props.
	 *
	 * @param props Props.
	 */
	public updateProps(props: Props): void {
		this.props = props;
	}

	/**
	 * Focus camera on items.
	 *
	 * @param forceUpdate Force update camera focus.
	 * @param items Items to focus on. By default: all items on scene.
	 */
	public focusCamera(
		forceUpdate = false,
		items: Map<string, ConnectableObject> = this.sceneItems
	): void {
		if (items.size > 0) {
			const FALLOFF = 3;

			const positionsExtends = this.getPositionsExtends(items);
			const selectAllFalloff = this.props.showSelectAll && this.props.deviceType !== 'mobile';
			const extrafalloff = this.props.showMeasurements || selectAllFalloff ? FALLOFF : 0;

			const ext = this.getCameraBoundingExtends(positionsExtends.extended, extrafalloff);
			const def = this.getCameraBoundingExtends(positionsExtends.default);

			const center: TransformPosition = {
				x: ((ext.max.x as number) + (ext.min.x as number)) / 2,
				z: ((ext.max.z as number) + (ext.min.z as number)) / 2,
			};
			this.view3d.setGroundPosition(center);
			const mobile = this.props.deviceType == 'mobile';

			this.mainCamera.focusOn(ext, mobile, forceUpdate);
			this.screenshotCamera.focusOn(def, mobile, true);
		} else {
			this.view3d.setGroundPosition(TransformVector.zero());
			this.mainCamera.reset();
		}
	}

	/**
	 * Creates a screenshot using the ScreenshotCamera.
	 *
	 * @param onReady Callback function.
	 */
	public createScreenshot(onReady: (screenshot: string) => void): void {
		this.debounce.set(
			'createScreenshot',
			() => {
				this.screenshotCamera.screenshot((screenshot) => {
					onReady(screenshot);
				}, this.props.deviceType == 'mobile');
			},
			300
		);
	}

	/**
	 * Fully resets camera and ground position to their initial state.
	 */
	public reset(): void {
		this.mainCamera.reset();
		this.view3d.setGroundPosition(TransformVector.zero());
	}

	/**
	 * Resets camera rotation.
	 */
	public resetRotation(): void {
		this.mainCamera.reset(['alpha', 'beta']);
	}

	/**
	 * Creates camera vertical rotation animation.
	 *
	 * @param rotation Y rotation in Rads.
	 */
	public animateCameraRotation(rotation: number, onAnimationEnd?: () => void): void {
		if (!rotation) {
			return;
		}
		this.mainCamera.rotateVertically(rotation, onAnimationEnd);
	}

	/**
	 * Creates camera vertical rotation.
	 *
	 * @param rotation Y rotation in Rads.
	 */
	public rotateCamera(rotation: number): void {
		this.mainCamera.setVerticalRotation(rotation);
	}

	/**
	 * Stops camera vertical rotation animation.
	 */
	public stopCameraRotationAnimation(): void {
		this.mainCamera.stopAnimation('verticalRotation');
	}

	/**
	 * Disables camera control for a user.
	 */
	public disableControl(): void {
		this.view3d.disableCameraControl();
	}

	/**
	 * Enables camera control for a user.
	 */
	public enableControl(): void {
		this.view3d.enableCameraControl();
	}

	/**
	 * Temporaly disable vertical camera rotation.
	 */
	public startCameraRotationLock(): void {
		this.mainCamera.setHorizontalLimits(0);
		this.cameraTimeout = window.setTimeout(() => {
			this.mainCamera.setHorizontalLimits(null);
		}, MAIN_CAMERA_ANIMATION_TIME);
	}

	/**
	 * Renable vertical camera rotation;.
	 */
	public clearCameraRotationLock(): void {
		clearTimeout(this.cameraTimeout);
		this.mainCamera.setHorizontalLimits(null);
	}

	/**
	 * Initialize cameras.
	 */
	private InitializeCameras(): void {
		this.mainCamera = this.view3d.getCamera();
		this.screenshotCamera = this.view3d.createCamera(SCREENSHOT_CAMERA_NAME);

		// Configuration
		this.mainCamera.setVerticalLimits(0);
		this.mainCamera.setTransitionTime(MAIN_CAMERA_ANIMATION_TIME);

		this.screenshotCamera.setVerticalLimits(0);
		this.screenshotCamera.setTransitionTime(SCREENSHOT_CAMERA_ANIMATION_TIME);

		// Setting Layer Mask to '2' will make the measurements ui invisible to the screenshot camera.
		this.screenshotCamera.setLayerMask(2);
	}

	/**
	 * Gets min and max position extends of items on scene. With extended models and with normal ones.
	 */
	private getPositionsExtends(
		objects: Map<string, ConnectableObject>,
		ignoreAutoObjects = true
	): { [key: string]: { min: TransformPosition; max: TransformPosition } } {
		const report = new Object() as {
			[key: string]: { min: TransformPosition; max: TransformPosition };
		};
		const min = new Object() as TransformPosition;
		const max = new Object() as TransformPosition;
		const bounds = ['default', 'extended'];

		bounds.forEach((b) => {
			objects.forEach((object) => {
				if (ignoreAutoObjects && this.connectionManager.isAutoConnectableModel(object.articleId)) {
					return;
				}
				const model = this.connectionManager.getModelData(object.articleId);
				if (!model || !model.bounds[b]) {
					return;
				}
				const rotation: TransformRotation = this.connectionManager.getWorldRotation(object);
				const position: TransformPosition = this.connectionManager.getWorldPosition(object);

				const size = model.bounds[b].size;

				const posOffset = TransformVector.rotate(
					model.bounds[b].position as TransformPosition,
					rotation
				);
				const modPosition = TransformVector.add(position, posOffset);

				const sizeOffsets = { x: size.width / 2, y: size.height / 2, z: size.depth / 2 };
				const rotatedOffset = TransformVector.rotate(sizeOffsets, rotation);

				const sub = TransformVector.sub(modPosition, rotatedOffset);
				const add = TransformVector.add(modPosition, rotatedOffset);

				if (min.x == undefined || min.x > Math.min(sub.x as number, add.x as number)) {
					min.x = Math.min(sub.x as number, add.x as number);
				}
				if (min.y == undefined || min.y > Math.min(sub.y as number, add.y as number)) {
					min.y = Math.min(sub.y as number, add.y as number);
				}
				if (min.z == undefined || min.z > Math.min(sub.z as number, add.z as number)) {
					min.z = Math.min(sub.z as number, add.z as number);
				}
				if (max.x == undefined || max.x < Math.max(sub.x as number, add.x as number)) {
					max.x = Math.max(sub.x as number, add.x as number);
				}
				if (max.y == undefined || max.y < Math.max(sub.y as number, add.y as number)) {
					max.y = Math.max(sub.y as number, add.y as number);
				}
				if (max.z == undefined || max.z < Math.max(sub.z as number, add.z as number)) {
					max.z = Math.max(sub.z as number, add.z as number);
				}
			});
			report[b] = {
				min: TransformVector.clone(min),
				max: TransformVector.clone(max),
			};
		});
		return report;
	}

	/**
	 * Gets Camera Bounding Extends to focus on.
	 */
	private getCameraBoundingExtends(
		positionsExtends: {
			min: TransformPosition;
			max: TransformPosition;
		},
		extrafalloff?: number
	): { min: TransformPosition; max: TransformPosition } {
		const { min, max } = positionsExtends;

		const falloff = extrafalloff ? extrafalloff + 1 : 1;
		if (
			Math.abs((max.x as number) - (min.x as number)) < CAMERAS_MIN_DISTANCE ||
			Math.abs((max.z as number) - (min.z as number)) < CAMERAS_MIN_DISTANCE ||
			falloff > 1
		) {
			if (
				positionsExtends &&
				positionsExtends.min &&
				positionsExtends.max &&
				positionsExtends.min.x &&
				positionsExtends.min.z &&
				positionsExtends.max.x &&
				positionsExtends.max.z
			) {
				positionsExtends.min.x -= falloff;
				positionsExtends.min.z -= falloff;
				positionsExtends.max.x += falloff;
				positionsExtends.max.z += falloff;
			}
			return positionsExtends;
		} else {
			return positionsExtends;
		}
	}
}
